import { Button, Form, Modal } from "antd";
import { useQueryClient } from "@tanstack/react-query";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactElement,
} from "react";
import { useTranslation } from "react-i18next";
import {
  createSearchParams,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import arrowBack from "../../assets/images/arrow-back.svg";
import leftIcon from "../../assets/images/left-arr.svg";
import rightGrayIcon from "../../assets/images/right-arr-gray.svg";
import rightWhiteIcon from "../../assets/images/right-arr-white.svg";
import { Loader } from "../../shared/components/Loader";
import CurrentSteps from "../../shared/layout/Steps";
import AxiosInstance from "../../shared/utils/axios";
import { fetchProtocolTemplateData } from "../protocols/protocolsService";
import { formatTranslation } from "../../shared/utils/translationUtils";
import "../style.less";
import { SUBMIT_PROTOCOL_LABEL } from "./constants";
import type {
  createProtocolFormType,
  createProtocolValidationDataType,
} from "./shared/interfaces/product-interfaces";
import "./style.less";
import ProductsComponent from "./step-components/ProductsComponent";
import RulesComponent from "./step-components/RulesComponent";
import SummaryComponent from "./step-components/SummaryComponent";
import TrialTypesComponent from "./step-components/TrialTypesComponent";
import { type ProtocolStepItemType } from "./types";
import {
  getCurrentStepNumber,
  getItems,
  getLocalizedCropId,
  getNextButtonLabel,
  getProtocolTypes,
  isBiologicalHasValidData,
  isSeedHasValidData,
  nextStep,
  prevStep,
} from "./utils";
import SaveTemplateModal from "./shared/components/SaveTemplateModal";
import { useTrialsRouter } from "../../shared/custom-hooks/useTrialsRouter";
import { useProtocolsRouter } from "../../shared/custom-hooks/useProtocolsRouter";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";
import {
  ProtocolCreationAmplitudeEvent,
  ProtocolTemplatesAmplitudeEvent,
  NavigationEvents,
} from "../../shared/utils/amplitudeEvents";
import { useFeatureFlags } from "../../context/LDFlagsContext";
import { useAuth } from "../../lib/auth/AuthContext";
import { getAuditFields } from "../../shared/utils/auditFields";

export type nextStepOptions = "draft" | "published" | "configure-trial";
type ProductTypeKey = "seed" | "biological" | "nutrient";

const PRODUCT_TYPE_ORDER: ProductTypeKey[] = ["seed", "biological", "nutrient"];
const PRODUCT_TYPE_BUTTON_LABELS: Record<ProductTypeKey, string> = {
  seed: "Seeds",
  biological: "CP and Biologicals",
  nutrient: "Nutrients",
};

const CreateProtocolContext = createContext<any>(undefined);

export const useCreateProtocolForm = (): any => {
  const context = useContext(CreateProtocolContext);
  if (context === undefined) {
    throw new Error(
      "useCreateProtocolForm must be used within a CreateProtocolProvider"
    );
  }
  return context;
};

const CreateProtocol = (): ReactElement => {
  const { t } = useTranslation();
  const { router: trialsRouter } = useTrialsRouter();
  const { router: protocolsRouter, workspaceId, orgId } = useProtocolsRouter();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [openBackModal, setOpenBackModal] = useState(false);
  const { enableProtocolTemplates } = useFeatureFlags();

  const showBackModal = (): void => {
    setOpenBackModal(true);
  };

  const handleOkBackModal = (): void => {
    protocolsRouter.goToProtocolsList();
  };

  const handleCancelModal = (): void => {
    setOpenBackModal(false);
  };

  const [form] = Form.useForm<createProtocolFormType>();
  const formValues = form.getFieldsValue(true);
  const { step } = useParams();

  const [formValidationData, setFormValidationData] =
    useState<createProtocolValidationDataType | null>(null);

  const [checkValue, setCheckValue] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] =
    useState<boolean>(false);

  const currentStepNumber = getCurrentStepNumber(step ?? "");

  const items = getItems();
  const [searchParams, setSearchParams] = useSearchParams();

  const protocolId = searchParams.get("protocol_id");
  const trackProtocolEvents = useTrackingEvent({
    page: "Protocols Page",
    group: "Protocols",
    module: "Protocols",
    protocol_id: protocolId ?? undefined,
    crop_type: formValues.protocolCrop ?? formValues.crop,
  });

  useEffect(() => {
    if (currentStepNumber === 4) {
      void trackProtocolEvents({
        event: NavigationEvents.SUMMARY_VIEWED,
        context: "Protocol Setup",
      });
    }
  }, [currentStepNumber, trackProtocolEvents]);

  const templateId = searchParams.get("template_id");
  const templateName = searchParams.get("template_name");
  const [templateData, setTemplateData] = useState<any>(null);
  const activeProductTypeFromQuery = searchParams.get("type");
  const protocolTypeFromQuery = searchParams.get("protocolType");

  const protocolTypes =
    Array.isArray(formValues?.protocolType) &&
    formValues.protocolType.length > 0
      ? formValues.protocolType
      : formValidationData?.protocolType ?? [];

  const selectedProductTypes = PRODUCT_TYPE_ORDER.filter((type) =>
    protocolTypes.includes(type)
  );

  const activeProductType = selectedProductTypes.includes(
    activeProductTypeFromQuery as ProductTypeKey
  )
    ? (activeProductTypeFromQuery as ProductTypeKey)
    : selectedProductTypes[0];

  const nextProductType = activeProductType
    ? selectedProductTypes[selectedProductTypes.indexOf(activeProductType) + 1]
    : undefined;

  const previousProductType = activeProductType
    ? selectedProductTypes[selectedProductTypes.indexOf(activeProductType) - 1]
    : undefined;

  const values = Form.useWatch([], form);

  const normalizeSeedsPayload = (
    seedDetailsValue: any,
    selectedTypes: string[]
  ): any => {
    if (!selectedTypes.includes("seed")) return null;

    const seedDetails: any = { ...(seedDetailsValue ?? {}) };

    const inferredDensityMode =
      seedDetails?.density_options?.length > 0 ? "options" : "range";

    const densityMode =
      (formValidationData as any)?.globalDensityMode ?? inferredDensityMode;

    const globalOptions =
      (formValidationData as any)?.globalDensityOptions ??
      seedDetails?.density_options;

    const products = seedDetails.products ?? [];

    if (densityMode === "range") {
      seedDetails.density_options = null;

      seedDetails.products = products.map((p: any) => ({
        ...p,
        density_options: null,
      }));

      if (!seedDetails?.density_range?.min) {
        seedDetails.density_range = null;
      }
    }

    if (densityMode === "options") {
      seedDetails.density_range = null;

      seedDetails.products = products.map((p: any) => ({
        ...p,
        density_range: null,
      }));

      if (seedDetails?.global_density_range === true) {
        seedDetails.density_options = globalOptions ?? [];

        seedDetails.products = seedDetails.products.map((p: any) => ({
          ...p,
          density_options: p?.density_options ?? seedDetails.density_options,
        }));
      }
    }

    const hasValidSeedData = isSeedHasValidData({
      ...(formValidationData ?? {}),
      ...seedDetails,
      globalDensityRange: seedDetails.global_density_range,
      globalDensityOptions: seedDetails.density_options,
      seed_details: seedDetails,
      products: seedDetails?.products,
      measure_unit: seedDetails?.measure_unit,
      unit: "test",
    });

    if (!hasValidSeedData) return null;
    const hasSeedProducts = seedDetails?.products?.length > 0;

    return {
      ...seedDetails,
      products: hasSeedProducts ? seedDetails.products : null,
    };
  };

  const normalizeBiologicalsPayload = (
    biologicals: any,
    selectedTypes: string[]
  ): any => {
    if (!selectedTypes.includes("biological")) return null;

    return isBiologicalHasValidData(biologicals ?? {}) ? biologicals : null;
  };

  const normalizeNutrientsPayload = (
    nutrients: any,
    selectedTypes: string[]
  ): any => {
    if (!selectedTypes.includes("nutrient")) return null;

    return formValidationData?.isNutrientDataValid === true
      ? nutrients ?? null
      : null;
  };

  useEffect(() => {
    const typesFromQuery = getProtocolTypes(protocolTypeFromQuery);
    if (typesFromQuery.length === 0) return;
    if (getProtocolTypes(form.getFieldValue("protocolType")).length > 0) return;
    form.setFieldsValue({ protocolType: typesFromQuery });
  }, [protocolTypeFromQuery, form]);

  useEffect(() => {
    const fetchTemplateData = async (): Promise<void> => {
      if (!templateId || protocolId) return;
      try {
        const data = await fetchProtocolTemplateData(
          templateId,
          orgId ?? "",
          workspaceId ?? ""
        );
        setTemplateData(data);
        const initialValues: Record<string, any> = {
          protocolType: data.protocol_type,
          protocolName: data.name,
          protocolDescription: data.description,
          protocolCrop: data.crop,
          protocolCountry: data.crop_details?.country,
          protocolCountries: data.crop_details?.countries,
          // The template carries the canonical crop id in `crop_id` and the
          // localized one in `localized_crop_ids`. Only the localized id lets
          // the backend re-derive `localized_crop_ids` when this protocol is
          // saved -- see getLocalizedCropId (CWTRC-2257).
          protocolCropId: getLocalizedCropId(data.crop_details),
          plotType: data.plot_type,
          objectives: data.objectives,
          trial_application_method: data.trial_application_method,
          seed_details: data.product_details?.seeds,
          nutrient_details: data.product_details?.nutrients,
          biologicals: data.product_details?.biologicals,
        };

        if (data.trial_application_method === "plot") {
          initialValues.plotMinWidth = data.plot_minimum_size?.width;
          initialValues.plotMinLength = data.plot_minimum_size?.length;
          initialValues.plotRecommendedWidth =
            data.plot_recommended_size?.width;
          initialValues.plotRecommendedLength =
            data.plot_recommended_size?.length;
        } else if (data.trial_application_method === "strip") {
          initialValues.stripMinWidth = data.strip_size?.min_width;
          initialValues.stripMaxWidth = data.strip_size?.max_width;
        }

        form.setFieldsValue(initialValues);

        setTimeout(() => {
          void form.validateFields();
          setFormValidationData((prev: any) => ({
            ...(prev ?? {}),
            protocolType: data.protocol_type,
            isNutrientDataValid: !!data.product_details?.nutrients,
            globalDensityMode:
              data?.product_details?.seeds?.density_options?.length > 0 ||
              data?.product_details?.seeds?.products[0]?.density_options
                ?.length > 0
                ? "options"
                : "range",
            originalTemplateSnapshot: {
              country: data.crop_details?.country,
              cropName: data.crop,
              cropId: data.crop_details?.crop_id,
              localizedCropIds: data.crop_details?.localized_crop_ids ?? [],
              seedDetails: data.product_details?.seeds ?? null,
              biologicals: data.product_details?.biologicals ?? null,
              plotMinimumSize: data.plot_minimum_size ?? null,
              plotRecommendedSize: data.plot_recommended_size ?? null,
              stripSize: data.strip_size ?? null,
            },
          }));
        }, 100);
      } catch (error) {
        console.error("Error fetching template data:", error);
      }
    };

    void fetchTemplateData();
  }, [templateId]);

  useEffect(() => {
    const fetchProtocolData = async (): Promise<void> => {
      if (protocolId) {
        try {
          const response = await AxiosInstance.get(`/protocol/${protocolId}`, {
            headers: {
              "org-id": orgId,
              "workspace-id": workspaceId ?? "",
            },
          });
          const protocolData = response.data;

          const initialValues = {
            protocolType: protocolData.protocol_type,
            protocolName: protocolData.name,
            seed_details: protocolData.product_details?.seeds,
            nutrient_details: protocolData.product_details?.nutrients,
            biologicals: protocolData.product_details?.biologicals,
            trial_application_method: protocolData.trial_application_method,
          };

          form.setFieldsValue(initialValues);

          setTimeout(() => {
            void form.validateFields();
            setFormValidationData((prev: any) => ({
              ...(prev ?? {}),
              globalDensityMode:
                protocolData?.product_details?.seeds?.density_options?.length >
                  0 ||
                protocolData?.product_details?.seeds?.products[0]
                  ?.density_options?.length > 0
                  ? "options"
                  : "range",
              protocolType: protocolData.protocol_type,
              isNutrientDataValid: !!protocolData.product_details?.nutrients,
              complete: protocolData.complete,
              status: protocolData.status,
            }));
          }, 100);
        } catch (error) {
          console.error("Error fetching protocol:", error);
        }
      }
    };

    void fetchProtocolData();
  }, [protocolId]);

  useEffect(() => {
    if (currentStepNumber === 1) {
      if (
        formValidationData?.protocolType &&
        formValidationData?.protocolType.length > 0
      ) {
        setCheckValue(true);
      } else {
        setCheckValue(false);
      }
    }
  }, [values, formValues, formValidationData?.protocolType, currentStepNumber]);

  useEffect(() => {
    if (currentStepNumber === 2) {
      if (formValidationData?.cropRevalidationInProgress) {
        setCheckValue(false);
        return;
      }

      form
        .validateFields({ validateOnly: true })
        .then(() => {
          if (formValues.objectives?.length > 0) setCheckValue(true);
          else setCheckValue(false);
        })
        .catch((err: { outOfDate?: boolean }) => {
          if (err?.outOfDate) return;
          setCheckValue(false);
        });
    }
  }, [values, formValues, formValidationData, currentStepNumber]);

  useEffect(() => {
    if (currentStepNumber === 3) {
      const seedRevalidationPending =
        formValidationData?.seedRevalidationRequired === true ||
        formValidationData?.seedRevalidationProcessing === true ||
        formValidationData?.clearProductsAndCalculations === true;

      const bioRevalidationPending =
        formValidationData?.bioRevalidationRequired === true ||
        formValidationData?.clearProductsAndCalculations === true;
      const seedsChecks =
        !seedRevalidationPending && isSeedHasValidData(formValidationData);

      const nutrientsChecks = Boolean(formValidationData?.isNutrientDataValid);

      const biologicalsChecks =
        !bioRevalidationPending &&
        Boolean(isBiologicalHasValidData(formValues?.biologicals ?? {}));

      const validityByType: Record<ProductTypeKey, boolean> = {
        seed: Boolean(seedsChecks),
        biological: biologicalsChecks,
        nutrient: nutrientsChecks,
      };

      const allSelectedProductTypesValid = selectedProductTypes.every(
        (type) => validityByType[type]
      );

      let currentTypeValid = false;

      if (activeProductType === "seed") {
        currentTypeValid = validityByType.seed;
      } else if (activeProductType === "biological") {
        currentTypeValid = validityByType.biological;
      } else if (activeProductType === "nutrient") {
        currentTypeValid = validityByType.nutrient;
      }

      setCheckValue(
        nextProductType ? currentTypeValid : allSelectedProductTypesValid
      );
    }
  }, [
    form,
    formValues,
    formValidationData,
    activeProductType,
    selectedProductTypes,
    nextProductType,
    currentStepNumber,
  ]);

  const getPrimaryNextLabel = (): string => {
    if (currentStepNumber === 3 && nextProductType) {
      return PRODUCT_TYPE_BUTTON_LABELS[nextProductType];
    }
    return getNextButtonLabel(currentStepNumber);
  };

  const getPrimaryBackLabel = (): string => {
    if (currentStepNumber === 1)
      return formatTranslation(t("createProtocol.cancel"));

    if (currentStepNumber === 3 && previousProductType) {
      return PRODUCT_TYPE_BUTTON_LABELS[previousProductType];
    }

    const previousStep = prevStep(step ?? "");
    return previousStep?.title ?? formatTranslation(t("createProtocol.back"));
  };

  const handleNextStep = async (
    nextStepOption: nextStepOptions
  ): Promise<void> => {
    const nextStepData: ProtocolStepItemType = nextStep(step ?? "");

    setIsUpdating(true);

    try {
      if (currentStepNumber === 1) {
        handleStepOne(nextStepData, formValues.protocolType);
      } else if (currentStepNumber === 2) {
        await handleStepTwo(formValues, nextStepData);
      } else if (currentStepNumber === 3 && protocolId !== null) {
        const isSaved = await handleStepThree(formValues, nextStepData);
        if (isSaved && activeProductType === "nutrient") {
          trackSavedProtocolNutrients(formValues);
        }
      } else if (
        step === items[items?.length - 1].path &&
        protocolId !== null
      ) {
        await handleFinalStep(nextStepOption);
      }
    } catch (error) {
      console.error("common.error", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleStepOne = (
    nextStepData: ProtocolStepItemType,
    protocolType: string[]
  ): void => {
    const params: Record<string, string> = {};

    if (protocolId !== null) params.protocol_id = protocolId;
    if (templateId) params.template_id = templateId;
    if (templateName) params.template_name = templateName;

    if (protocolId === null && !templateId) {
      params.protocolType = protocolType.toString();
    }

    const search = createSearchParams(params).toString();

    if (protocolId !== null) {
      protocolsRouter.goToProtocolsEditPage(
        search,
        protocolId,
        nextStepData.path
      );
    } else {
      protocolsRouter.goToProtocolsCreatePage(search, nextStepData.path);
    }
  };

  const handleStepTwo = async (
    formValues: any,
    nextStepData: ProtocolStepItemType
  ): Promise<void> => {
    const selectedTypes = formValues?.protocolType ?? [];

    const payload: any = {
      org_id: orgId,
      workspace_id: workspaceId ?? "",
      name: formValues.protocolName,
      description: formValues.protocolDescription,
      crop: formValues.protocolCrop,
      crop_details: {
        countries: formValues.protocolCountries,
        country: formValues.protocolCountry,
        crop_id: formValues.protocolCropId,
      },
      protocol_type: formValues.protocolType,
      plot_type: formValues.plotType,
      objectives: formValues.objectives,
      template_id: templateId ?? null,
      trial_application_method: formValues.trial_application_method,
      ...(formValues.trial_application_method === "plot"
        ? {
            plot_minimum_size: getPlotSize(
              formValues.plotMinWidth,
              formValues.plotMinLength
            ),
            plot_recommended_size: getPlotSize(
              formValues.plotRecommendedWidth,
              formValues.plotRecommendedLength
            ),
            strip_size: null,
          }
        : {
            strip_size: getStripSize(
              formValues.stripMinWidth,
              formValues.stripMaxWidth
            ),
            plot_recommended_size: null,
            plot_minimum_size: null,
          }),
    };

    if (protocolId === null) {
      if (templateId) {
        payload.product_details = {
          seeds: normalizeSeedsPayload(formValues?.seed_details, selectedTypes),
          biologicals: normalizeBiologicalsPayload(
            formValues?.biologicals,
            selectedTypes
          ),
          nutrients: normalizeNutrientsPayload(
            formValues?.nutrient_details,
            selectedTypes
          ),
        };
      }

      await makeApiCall(
        "/protocol",
        "post",
        payload,
        nextStepData,
        undefined,
        (savedProtocolId) => {
          trackProtocolRulesSaved(payload, savedProtocolId);
        }
      );
    } else {
      await makeApiCall(
        `/protocol/${protocolId}`,
        "patch",
        payload,
        nextStepData,
        undefined,
        (savedProtocolId) => {
          trackProtocolRulesSaved(payload, savedProtocolId);
        }
      );
    }
  };

  const trackProtocolRulesSaved = (
    payload: Record<string, any>,
    savedProtocolId: string | undefined
  ): void => {
    const targets = Array.isArray(payload.objectives)
      ? payload.objectives.flatMap((objective: any) => objective.targets ?? [])
      : [];
    void trackProtocolEvents({
      event: ProtocolCreationAmplitudeEvent.PROTOCOL_RULES_SAVED,
      protocol_id: savedProtocolId,
      crop_type: payload.crop,
      app_method:
        payload.trial_application_method === "strip" ? "Strips" : "Plots",
      min_sizes: {
        plot: payload.plot_minimum_size,
        strip: payload.strip_size,
      },
      crop: payload.crop,
      country: payload.crop_details?.country,
      targets,
    });
  };

  const trackSavedProtocolNutrients = (formValues: any): void => {
    const nutrients = formValues?.nutrient_details;
    if (!nutrients) return;
    void trackProtocolEvents({
      event: ProtocolCreationAmplitudeEvent.PROTOCOL_NUTRIENT_SET,
      protocol_id: protocolId ?? undefined,
      crop_type: formValues.protocolCrop ?? formValues.crop,
      nutrient_type: nutrients.products?.[0]?.nutrient_code,
      target_rate: nutrients.application_range,
      intervals: nutrients.interval_distance,
      level: nutrients.number_of_intervals,
    });
  };

  const handleStepThree = async (
    formValues: any,
    nextStepData?: ProtocolStepItemType
  ): Promise<boolean> => {
    const payload = {
      product_details: {
        seeds: normalizeSeedsPayload(formValues?.seed_details, protocolTypes),
        biologicals: normalizeBiologicalsPayload(
          formValues?.biologicals,
          protocolTypes
        ),
        nutrients: normalizeNutrientsPayload(
          formValues?.nutrient_details,
          protocolTypes
        ),
      },
    };

    if (protocolId === null) return false;

    return await makeApiCall(
      `/protocol/${protocolId}`,
      "patch",
      payload,
      nextStepData
    );
  };

  const handleNextProductType = async (
    productType: ProductTypeKey
  ): Promise<void> => {
    setIsUpdating(true);

    try {
      const isSaved = await handleStepThree(form.getFieldsValue(true));
      if (!isSaved || !activeProductType) return;
      if (activeProductType === "nutrient") {
        trackSavedProtocolNutrients(form.getFieldsValue(true));
      }

      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set("type", productType);
      setSearchParams(updatedSearchParams);
    } catch (error) {
      console.error("common.error", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFinalStep = async (
    nextStepOption: nextStepOptions
  ): Promise<void> => {
    const payload =
      nextStepOption === "draft"
        ? { status: "draft" }
        : { status: "published" };

    if (protocolId !== null) {
      const isSaved = await makeApiCall(
        `/protocol/${protocolId}`,
        "patch",
        payload,
        undefined,
        nextStepOption
      );
      if (isSaved && payload.status === "published") {
        void trackProtocolEvents({
          event: ProtocolCreationAmplitudeEvent.PROTOCOL_PUBLISHED,
          protocol_id: protocolId,
          crop_type: formValues.protocolCrop ?? formValues.crop,
          protocol_name: form.getFieldValue("protocolName"),
        });
      }
    }
  };

  const handleError = (messageKey: string, error?: any): void => {
    toast.error(formatTranslation(t(messageKey)));

    if (error) {
      console.error(formatTranslation(t(messageKey)), error);
    } else {
      console.error(formatTranslation(t(messageKey)));
    }
  };

  const makeApiCall = async (
    url: string,
    method: "post" | "patch",
    payload: any,
    nextStepData?: ProtocolStepItemType,
    nextStepOption?: nextStepOptions,
    onSaved?: (savedProtocolId: string | undefined) => void
  ): Promise<boolean> => {
    try {
      const config = {
        headers: {
          "org-id": orgId ?? "",
          "workspace-id": workspaceId ?? "",
        },
      };

      const response = await AxiosInstance[method](
        url,
        { ...payload, ...getAuditFields(method, user?.id) },
        config
      );

      if (response?.status !== 200 && !response?.data?.id) {
        throw new Error(
          response?.data?.errors?.[0]?.message ?? "common.error.patch"
        );
      }

      // A create/patch here can change a protocol's status (draft/published),
      // which shifts the Protocols/Trials list Summary Insights counts.
      void queryClient.invalidateQueries({ queryKey: ["summaryInsights"] });

      onSaved?.(response?.data?.id ?? protocolId ?? undefined);

      if (nextStepData) {
        const newProtocolId = response?.data?.id ?? protocolId;
        const nextParams: Record<string, string> = {
          protocol_id: newProtocolId as string,
        };

        if (templateId) nextParams.template_id = templateId;

        setSearchParams(nextParams);

        protocolsRouter.goToProtocolsEditPage(
          createSearchParams(nextParams).toString(),
          newProtocolId as string,
          nextStepData.path
        );
      } else if (nextStepOption) {
        nextStepOption === "configure-trial"
          ? trialsRouter.goToTrialsCreatePage()
          : protocolsRouter.goToProtocolsList();
      }

      return true;
    } catch (error: any) {
      if (error?.response?.data?.errors?.length > 0) {
        handleError("protocol.toastMessage.patch", error);
      } else {
        handleError(
          method === "patch" ? "common.error.patch" : "common.error.post",
          error
        );
      }

      return false;
    }
  };

  const getPlotSize = (width: number, length: number): any => {
    return isNaN(width ?? NaN) || isNaN(length ?? NaN) ? {} : { width, length };
  };

  const getStripSize = (minWidth: number, maxWidth: number): any => {
    return isNaN(minWidth ?? NaN) || isNaN(maxWidth ?? NaN)
      ? {}
      : { min_width: minWidth, max_width: maxWidth };
  };

  const handlePrevStep = (): void => {
    if (currentStepNumber === 3 && previousProductType) {
      const updatedSearchParams = new URLSearchParams(searchParams);
      updatedSearchParams.set("type", previousProductType);
      setSearchParams(updatedSearchParams);
      return;
    }

    const pervStepData: ProtocolStepItemType | any = prevStep(step ?? "");

    if (pervStepData !== undefined) {
      const params: Record<string, string> = {};

      if (protocolId !== null) params.protocol_id = protocolId;
      if (templateId) params.template_id = templateId;
      if (templateName) params.template_name = templateName;
      if (protocolTypeFromQuery) params.protocolType = protocolTypeFromQuery;

      const search = createSearchParams(params).toString();
      const previousStepSearch = search === "" ? undefined : search;

      if (protocolId !== null) {
        protocolsRouter.goToProtocolsEditPage(
          previousStepSearch,
          protocolId,
          pervStepData.path
        );
      } else {
        protocolsRouter.goToProtocolsCreatePage(
          previousStepSearch,
          pervStepData.path
        );
      }
    } else {
      setOpenBackModal(true);
    }
  };

  const handleOpenTemplateModal = (): void => {
    void trackProtocolEvents({
      event:
        ProtocolTemplatesAmplitudeEvent.SAVE_AS_TEMPLATE_WORKSPACE_UNIVERSAL,
      originAction: "save-as-template-workspace-universal",
    });
    setIsTemplateModalOpen(true);
  };

  const handleCloseTemplateModal = (): void => {
    setIsTemplateModalOpen(false);
  };

  return (
    <div className="createProtocol-page">
      {isUpdating && (
        <Loader mask message={formatTranslation(t("createProtocol.saving"))} />
      )}

      <Modal
        open={openBackModal}
        title={formatTranslation(t("modal.goBackToListHeaderProtocol"))}
        onOk={handleOkBackModal}
        onCancel={handleCancelModal}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <Button onClick={handleCancelModal}>
              {formatTranslation(t("modal.cancel"))}
            </Button>
            <Button
              className="ct-bg-red ct-text-white"
              onClick={handleOkBackModal}
            >
              {formatTranslation(t("modal.leavePage"))}
            </Button>
          </>
        )}
      >
        <p>{formatTranslation(t("modal.goBackToList"))}</p>
        <p>{formatTranslation(t("modal.goBackToTrailFooter"))}</p>
      </Modal>

      <SaveTemplateModal
        open={isTemplateModalOpen}
        protocolId={protocolId}
        protocolName={form.getFieldValue("protocolName") ?? ""}
        orgId={orgId ?? ""}
        workspaceId={workspaceId ?? ""}
        onClose={handleCloseTemplateModal}
      />

      <div className="createProtocol-header">
        <div className="breadcrumbs" data-testid="breadcrumbs-div">
          <Button
            className="Left-button-icon"
            onClick={() => {
              showBackModal();
            }}
          >
            <img
              src={arrowBack}
              alt={formatTranslation(t("createProtocol.leftIcon"))}
            />
          </Button>
          <p>
            {formValidationData?.isNewProtocol
              ? formatTranslation(t("createProtocol.title"))
              : formatTranslation(t("createProtocol.titleEdit"))}
          </p>
        </div>

        <div className="stepper-common">
          <CurrentSteps current={currentStepNumber - 1} items={items} />
        </div>
      </div>

      <div className="createProtocol-body">
        <Form
          data-testid="form-basic"
          name="basic"
          form={form}
          style={
            currentStepNumber === 3 || currentStepNumber === 4
              ? { width: "100%" }
              : {}
          }
          layout="vertical"
          initialValues={{
            protocolType: null,
            plotRecommendedWidth: null,
            plotRecommendedLength: null,
            plotMinWidth: null,
            plotMinLength: null,
            stripMinWidth: null,
            stripMaxWidth: null,
          }}
        >
          <ToastContainer stacked />

          <div className="router-outlet">
            <CreateProtocolContext.Provider
              value={{
                form,
                formValues,
                formValidationData,
                setFormValidationData,
              }}
            >
              {step === "trial-type" && (
                <TrialTypesComponent templateData={templateData} />
              )}
              {step === "rules" && <RulesComponent />}
              {step === "products" && <ProductsComponent />}
              {step === "summary" && <SummaryComponent />}
            </CreateProtocolContext.Provider>
          </div>
        </Form>
      </div>

      <div className="bottom-sec-btn">
        <div className="bottom-sec-left-actions">
          <Button
            size="large"
            className="cancel-btn"
            data-testid="cancel-btn"
            onClick={handlePrevStep}
          >
            <img
              src={leftIcon}
              style={{ color: "black" }}
              alt={formatTranslation(t("createProtocol.leftIcon"))}
            />
            {getPrimaryBackLabel()}
          </Button>

          {formValidationData &&
            (formValidationData.status === "published" ||
              formValidationData.complete === true) &&
            currentStepNumber === 4 &&
            protocolId !== null &&
            enableProtocolTemplates && (
              <Button
                size="large"
                className="next-btn draft-btn template-btn"
                onClick={handleOpenTemplateModal}
              >
                {formatTranslation(t("createProtocol.saveAsTemplate"))}
              </Button>
            )}
        </div>

        <div className="bottom-sec-right-actions split-button">
          {currentStepNumber === 4 && protocolId !== null ? (
            <>
              <Button
                size="large"
                className="next-btn draft-btn"
                onClick={() => {
                  void handleNextStep("draft");
                }}
              >
                {formatTranslation(t("createProtocol.dropdownOption1"))}
              </Button>

              <Button
                size="large"
                className="next-btn publish-btn"
                type="primary"
                onClick={() => {
                  void handleNextStep("published");
                }}
              >
                {formatTranslation(t("createProtocol.dropdownOption2"))}
              </Button>
            </>
          ) : (
            <Button
              size="large"
              onClick={() => {
                void (async () => {
                  if (
                    currentStepNumber === 3 &&
                    nextProductType &&
                    checkValue
                  ) {
                    await handleNextProductType(nextProductType);
                    return;
                  }

                  await handleNextStep("configure-trial");
                })();
              }}
              className={`next-btn ${
                currentStepNumber === 4 ? "protocol-summary-btn" : ""
              } ${checkValue ? "" : "active"}`}
              htmlType="submit"
              data-testid="next-btn"
              disabled={!checkValue}
              loading={Boolean(formValidationData?.cropRevalidationInProgress)}
            >
              {getPrimaryNextLabel()}
              {getPrimaryNextLabel() !== SUBMIT_PROTOCOL_LABEL && (
                <img
                  src={checkValue ? rightWhiteIcon : rightGrayIcon}
                  alt={formatTranslation(t("createProtocol.leftIcon"))}
                />
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateProtocol;
